import { useCallback, useEffect, useMemo, useState } from 'react';

export const useArchiveSelection = <T extends { id: string }>(items: T[]) => {
  const [selectedById, setSelectedById] = useState(() => new Map<string, T>());

  // Refresh cached docs when visible items update, but keep selections that left the list.
  useEffect(() => {
    setSelectedById((prev) => {
      if (prev.size === 0) return prev;

      let changed = false;
      const next = new Map(prev);
      for (const item of items) {
        if (next.has(item.id) && next.get(item.id) !== item) {
          next.set(item.id, item);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const selectedIds = useMemo(() => new Set(selectedById.keys()), [selectedById]);
  const selectedItems = useMemo(() => [...selectedById.values()], [selectedById]);
  const selectedCount = selectedItems.length;
  const hasSelections = selectedCount > 0;
  const allVisibleSelected = items.length > 0 && items.every((item) => selectedById.has(item.id));
  const someVisibleSelected = items.some((item) => selectedById.has(item.id));

  const clearSelection = useCallback(() => {
    setSelectedById(new Map());
  }, []);

  const setSelectedItems = useCallback((next: T[]) => {
    setSelectedById(new Map(next.map((item) => [item.id, item])));
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (items.length > 0 && items.every((item) => next.has(item.id))) {
        for (const item of items) {
          next.delete(item.id);
        }
      } else {
        for (const item of items) {
          next.set(item.id, item);
        }
      }
      return next;
    });
  }, [items]);

  const toggleItem = useCallback((item: T) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, item);
      }
      return next;
    });
  }, []);

  // Callers must drop items they remove from the dataset: an absent item is
  // otherwise treated as merely filtered out and stays selected.
  const deselectItem = useCallback((item: T) => {
    setSelectedById((prev) => {
      if (!prev.has(item.id)) return prev;

      const next = new Map(prev);
      next.delete(item.id);
      return next;
    });
  }, []);

  return {
    selectedIds,
    selectedItems,
    selectedCount,
    hasSelections,
    allVisibleSelected,
    someVisibleSelected,
    toggleItem,
    toggleAllVisible,
    deselectItem,
    clearSelection,
    setSelectedItems,
  };
};
